import { useCallback } from 'react'
import { useIntl, type MessageDescriptor } from 'react-intl'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import useMountedEffect from 'hooks/useMountedEffect'
import { markTripChatVoiceInputUsed } from 'storage/tripChatVoicePrompts'
import { trackVoiceInputUsed } from 'tripPlanner/utils/chatAnalytics'
import composerVoiceMessages from '../composerVoiceMessages'
import { useMicrophoneTranscriber, type VoiceInputError } from './useMicrophoneTranscriber'

const ERROR_MESSAGES: Record<VoiceInputError, MessageDescriptor> = {
  'microphone-unavailable': composerVoiceMessages.microphoneUnavailable,
  'transcription-failed': composerVoiceMessages.transcriptionFailed,
}

interface Options {
  chatId?: number
  /** Adds the transcript to the composer's message */
  onTranscript: (transcript: string) => void
  /** The composer's focus decision, shared with sending a message */
  settleFocus: () => void
}

/** Voice input for a chat composer: records, transcribes into the message, and reports failures. */
export function useVoiceComposer({ chatId, onTranscript, settleFocus }: Options) {
  const intl = useIntl()

  const handleTranscription = useCallback((transcript: string) => {
    onTranscript(transcript)
    markTripChatVoiceInputUsed()
    trackVoiceInputUsed(chatId)
    settleFocus()
  }, [chatId, onTranscript, settleFocus])

  const handleError = useCallback((error: VoiceInputError) => {
    showSnackbar(intl.formatMessage(ERROR_MESSAGES[error]), 'critical')
  }, [intl])

  const recorder = useMicrophoneTranscriber({ onTranscription: handleTranscription, onError: handleError })

  // Switching chats keeps the composer mounted, so a recording must not carry into the next chat.
  useMountedEffect(() => {
    recorder.cancelRecording()
  }, [chatId])

  return recorder
}
